// src/features/MediaLibrary/MediaBrowser.js - Images only version
import React, { useState, useEffect } from 'react';
import MediaService from './MediaService';
import './MediaBrowser.css';

const MediaBrowser = ({
  onSelectMedia,
  selectedMediaId = null,
  showSearchBar = true,
  showCategoryFilter = true,
  unitId = null
}) => {
  const [imageItems, setImageItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState(['all']);
  const [selectedItem, setSelectedItem] = useState(null);
  const [previewModal, setPreviewModal] = useState(null);
  const [sortField, setSortField] = useState('uploadedAt');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    loadImageItems();
  }, [selectedMediaId]);

  const loadImageItems = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const assets = await MediaService.getAllMediaAssets();
      
      // Filter for images only
      const imageAssets = assets.filter(asset => {
        // Check type field first
        if (asset.type === 'image') return true;
        
        // Fallback to URL extension check
        if (asset.url && typeof asset.url === 'string') {
          const url = asset.url.toLowerCase();
          return url.endsWith('.jpg') || url.endsWith('.jpeg') || 
                 url.endsWith('.png') || url.endsWith('.gif') || 
                 url.endsWith('.svg') || url.endsWith('.webp');
        }
        
        return false;
      });
      
      setImageItems(imageAssets);
      
      // Find selected item if ID is provided
      if (selectedMediaId) {
        const selected = imageAssets.find(item => item.id === selectedMediaId);
        if (selected) {
          setSelectedItem(selected);
        }
      }
      
      // Extract unique categories
      const uniqueCategories = [...new Set(imageAssets
        .map(item => item.category)
        .filter(Boolean))];
      setCategories(['all', ...uniqueCategories]);
      
      setLoading(false);
    } catch (err) {
      console.error('Error loading images:', err);
      setError('Failed to load images. Please try again.');
      setLoading(false);
    }
  };

  const handleItemSelect = async (item) => {
    try {
      setSelectedItem(item);
      
      // Track usage if unitId is provided (for future implementation)
      if (unitId && item.id) {
        await MediaService.addUnitUsage(item.id, unitId);
      }
      
      if (onSelectMedia) {
        onSelectMedia(item);
      }
    } catch (err) {
      console.error('Error selecting image:', err);
      setError('Failed to select image.');
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getFilteredAndSortedItems = () => {
    let filtered = imageItems.filter(item => {
      // Search filter
      const matchesSearch = !searchTerm || 
        (item.filename && item.filename.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.name && item.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.altText && item.altText.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.tags && Array.isArray(item.tags) && item.tags.some(tag => 
          tag.toLowerCase().includes(searchTerm.toLowerCase())
        ));
      
      // Category filter
      const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });

    // Sort items
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'filename':
          aValue = (a.filename || a.name || '').toLowerCase();
          bValue = (b.filename || b.name || '').toLowerCase();
          break;
        case 'uploadedAt':
          aValue = a.uploadedAt ? 
            (typeof a.uploadedAt === 'object' && a.uploadedAt.seconds ? 
              new Date(a.uploadedAt.seconds * 1000) : 
              new Date(a.uploadedAt)) : 
            new Date(0);
          bValue = b.uploadedAt ? 
            (typeof b.uploadedAt === 'object' && b.uploadedAt.seconds ? 
              new Date(b.uploadedAt.seconds * 1000) : 
              new Date(b.uploadedAt)) : 
            new Date(0);
          break;
        case 'fileSize':
          aValue = a.fileSize || 0;
          bValue = b.fileSize || 0;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const kb = bytes / 1024;
    if (kb < 1000) return `${Math.round(kb)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
  };

  const formatDate = (date) => {
    if (!date) return '';
    
    const d = typeof date === 'object' && date.seconds ? 
      new Date(date.seconds * 1000) : 
      new Date(date);
    
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
  };

  const filteredItems = getFilteredAndSortedItems();

  return (
    <div className="media-browser">
      {/* Search and filter controls */}
      {(showSearchBar || showCategoryFilter) && (
        <div className="browser-controls">
          {showSearchBar && (
            <div className="search-control">
              <input 
                type="text"
                placeholder="Search images by name, tags, description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          )}
          
          {showCategoryFilter && categories.length > 1 && (
            <div className="category-control">
              <select 
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="category-select"
              >
                {categories.map((category, index) => (
                  <option key={index} value={category}>
                    {category === 'all' ? 'All Categories' : 
                      category.charAt(0).toUpperCase() + category.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          <button 
            onClick={loadImageItems}
            className="refresh-btn"
            disabled={loading}
          >
            🔄 Refresh
          </button>
        </div>
      )}
      
      {/* Results summary */}
      <div className="results-summary">
        {filteredItems.length} image{filteredItems.length !== 1 ? 's' : ''}
        {searchTerm && ` matching "${searchTerm}"`}
      </div>
      
      {/* Content area */}
      <div className="browser-content">
        {loading ? (
          <div className="browser-message loading">Loading images...</div>
        ) : error ? (
          <div className="browser-message error">{error}</div>
        ) : filteredItems.length === 0 ? (
          <div className="browser-message empty">
            {searchTerm 
              ? `No images match your search.` 
              : `No images found in the library.`}
          </div>
        ) : (
          <div className="media-table-container">
            <table className="media-table">
              <thead>
                <tr>
                  <th></th>
                  <th 
                    className={`sortable ${sortField === 'filename' ? 'active' : ''}`}
                    onClick={() => handleSort('filename')}
                  >
                    Filename 
                    {sortField === 'filename' && (
                      <span className="sort-indicator">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th 
                    className={`sortable ${sortField === 'uploadedAt' ? 'active' : ''}`}
                    onClick={() => handleSort('uploadedAt')}
                  >
                    Upload Date
                    {sortField === 'uploadedAt' && (
                      <span className="sort-indicator">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th>Tags</th>
                  <th 
                    className={`sortable ${sortField === 'fileSize' ? 'active' : ''}`}
                    onClick={() => handleSort('fileSize')}
                  >
                    Size
                    {sortField === 'fileSize' && (
                      <span className="sort-indicator">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map(item => (
                  <tr 
                    key={item.id}
                    className={`media-row ${selectedItem && selectedItem.id === item.id ? 'selected' : ''}`}
                    onClick={() => handleItemSelect(item)}
                  >
                    <td className="file-type-cell">
                      🖼️
                    </td>
                    <td className="filename-cell">
                      <div className="filename-container">
                        <span className="filename" title={item.filename || item.name || 'Unnamed'}>
                          {item.filename || item.name || 'Unnamed'}
                        </span>
                        {item.dimensions && (
                          <span className="dimensions">{item.dimensions}</span>
                        )}
                      </div>
                    </td>
                    <td className="date-cell">
                      {formatDate(item.uploadedAt)}
                    </td>
                    <td className="tags-cell">
                      {item.tags && item.tags.length > 0 ? (
                        <div className="tags-container">
                          {item.tags.slice(0, 3).map((tag, index) => (
                            <span key={index} className="tag-badge">{tag}</span>
                          ))}
                          {item.tags.length > 3 && (
                            <span className="tag-badge more">+{item.tags.length - 3}</span>
                          )}
                        </div>
                      ) : (
                        <span className="no-tags">No tags</span>
                      )}
                    </td>
                    <td className="size-cell">
                      {formatFileSize(item.fileSize)}
                    </td>
                    <td className="actions-cell">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewModal(item);
                        }}
                        className="preview-btn"
                        title="Preview image"
                      >
                        👁️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewModal && (
        <div className="preview-modal" onClick={() => setPreviewModal(null)}>
          <div className="preview-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="close-button" 
              onClick={() => setPreviewModal(null)}
            >
              ×
            </button>
            
            <h3>{previewModal.filename || previewModal.name || 'Unnamed'}</h3>
            
            <div className="preview-media">
              <img 
                src={previewModal.url} 
                alt={previewModal.altText || previewModal.filename || 'Preview'} 
              />
            </div>
            
            <div className="preview-details">
              {previewModal.description && <p><strong>Description:</strong> {previewModal.description}</p>}
              {previewModal.altText && <p><strong>Alt Text:</strong> {previewModal.altText}</p>}
              {previewModal.tags && previewModal.tags.length > 0 && (
                <p><strong>Tags:</strong> {previewModal.tags.join(', ')}</p>
              )}
              {previewModal.category && <p><strong>Category:</strong> {previewModal.category}</p>}
              {previewModal.dimensions && <p><strong>Dimensions:</strong> {previewModal.dimensions}</p>}
              {previewModal.fileSize && <p><strong>File Size:</strong> {formatFileSize(previewModal.fileSize)}</p>}
              <p><strong>Upload Date:</strong> {formatDate(previewModal.uploadedAt)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaBrowser;